import { normalizeStoredFilePath } from "../../../../../infrastructure/utils/resolveUploadUrl";
import { normalizeAssignmentPermissions } from "../../../../components/request/requestAssignmentDisplay";
import {
  formatLocalDateForApi,
  toJalaliDate,
} from "../../../../middleware/middleware";
import { enrichApplicantIdentityFields } from "../../solarent-operator/all-requests/allRequestsPageUtils";
import { CHECK_NEW_REQUEST_CONFIG } from "./checkNewRequestConfig";

export const getGenderDisplay = (gender) => {
  if (gender === 1 || gender === "Male" || gender === "1") return "مرد";
  if (gender === 2 || gender === "Female" || gender === "2") return "زن";
  return "";
};

export const normalizeGenderFormValue = (gender) => {
  if (gender === 1 || gender === "1" || gender === "Male") return "1";
  if (gender === 2 || gender === "2" || gender === "Female") return "2";
  return "";
};

export const parseApplicantBirthDate = (value) => {
  if (!value || value === "0001-01-01T00:00:00") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatBirthDateForApi = (value) => formatLocalDateForApi(value);

const pickString = (source, ...keys) => {
  if (!source) return "";
  for (const key of keys) {
    const value = source[key];
    if (value != null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
};

const LOCATION_USAGE_TYPE_BY_NAME = {
  Residential: 1,
  Commercial: 2,
  Office: 3,
  Industrial: 4,
  Agricultural: 5,
  GardenAndVilla: 6,
  Religious: 7,
  Educational: 8,
  Other: 9,
};

const OWNERSHIP_STATUS_BY_NAME = {
  Owner: 1,
  Tenant: 2,
  HasUsagePermit: 3,
  NeedsReview: 4,
};

const PLATFORM_ACQUAINTANCE_SOURCE_BY_NAME = {
  Mehrsan: 1,
  SocialNetworks: 2,
  Friends: 3,
  GoogleSearch: 4,
  Other: 5,
};

const POWER_PLANT_TYPE_BY_NAME = {
  GridConnected: 1,
  OffGrid: 2,
  Hybrid: 3,
  HybridWithoutBattery: 4,
};

const PLANT_CAPACITY_BY_NAME = {
  P5KW: 5,
  P10KW: 10,
  P15KW: 15,
  P20KW: 20,
};

const PLANT_CAPACITY_NUMBER_TO_NAME = {
  5: "P5KW",
  10: "P10KW",
  15: "P15KW",
  20: "P20KW",
};

const normalizePlantCapacityDropdownValue = (raw) => {
  if (raw == null || raw === "") return "";
  const str = String(raw).trim();
  if (PLANT_CAPACITY_BY_NAME[str] != null) return str;
  if (/^\d+$/.test(str)) {
    return PLANT_CAPACITY_NUMBER_TO_NAME[Number(str)] ?? str;
  }
  return str;
};

/** API StringEnumConverter returns names; dropdowns use numeric string values. */
export const normalizeEnumToDropdownValue = (raw, nameToNumberMap) => {
  if (raw == null || raw === "") return "";
  if (typeof raw === "number" && !Number.isNaN(raw)) return String(raw);
  const str = String(raw).trim();
  if (/^\d+$/.test(str)) return str;
  const mapped = nameToNumberMap[str];
  return mapped != null ? String(mapped) : "";
};

/** Normalize enum-backed request fields for form dropdowns. */
export const normalizeRequestEnumFields = (rawRequest) => {
  if (!rawRequest) return rawRequest;
  return {
    ...rawRequest,
    powerPlantType: normalizeEnumToDropdownValue(
      rawRequest.powerPlantType ?? rawRequest.PowerPlantType,
      POWER_PLANT_TYPE_BY_NAME
    ),
    locationUsageType: normalizeEnumToDropdownValue(
      rawRequest.locationUsageType ?? rawRequest.LocationUsageType,
      LOCATION_USAGE_TYPE_BY_NAME
    ),
    ownershipStatus: normalizeEnumToDropdownValue(
      rawRequest.ownershipStatus ?? rawRequest.OwnershipStatus,
      OWNERSHIP_STATUS_BY_NAME
    ),
    platformAcquaintanceSource: normalizeEnumToDropdownValue(
      rawRequest.platformAcquaintanceSource ??
        rawRequest.PlatformAcquaintanceSource,
      PLATFORM_ACQUAINTANCE_SOURCE_BY_NAME
    ),
    plantCapacity: normalizePlantCapacityDropdownValue(
      rawRequest.plantCapacity ?? rawRequest.PlantCapacity
    ),
    powerPlantTypeTitle:
      rawRequest.powerPlantTypeTitle ?? rawRequest.PowerPlantTypeTitle ?? "",
    locationUsageTypeTitle:
      rawRequest.locationUsageTypeTitle ?? rawRequest.LocationUsageTypeTitle ?? "",
    ownershipStatusTitle:
      rawRequest.ownershipStatusTitle ?? rawRequest.OwnershipStatusTitle ?? "",
    platformAcquaintanceSourceTitle:
      rawRequest.platformAcquaintanceSourceTitle ??
      rawRequest.PlatformAcquaintanceSourceTitle ??
      "",
    needsLoan:
      rawRequest.needsLoan !== undefined
        ? rawRequest.needsLoan
        : rawRequest.NeedsLoan !== undefined
          ? rawRequest.NeedsLoan
          : false,
    loanGuaranteeChequeIntent:
      rawRequest.loanGuaranteeChequeIntent ??
      rawRequest.LoanGuaranteeChequeIntent ??
      "",
    loanGuaranteeChequeIntentTitle:
      rawRequest.loanGuaranteeChequeIntentTitle ??
      rawRequest.LoanGuaranteeChequeIntentTitle ??
      "",
  };
};

const toNullableEnumNumber = (value) =>
  value !== "" && value != null && !Number.isNaN(Number(value))
    ? Number(value)
    : null;

import { parsePlantCapacityForApi } from "../customerRequest/plantCapacityFormConstants";

/** camelCase + PascalCase enum fields for ASP.NET model binding. */
export const buildRequestEnumSubmitFields = (formValue) => {
  const powerPlantType = toNullableEnumNumber(formValue.powerPlantType);
  const locationUsageType = toNullableEnumNumber(formValue.locationUsageType);
  const ownershipStatus = toNullableEnumNumber(formValue.ownershipStatus);
  const platformAcquaintanceSource = toNullableEnumNumber(
    formValue.platformAcquaintanceSource
  );
  const applicantGoalFlags = toNullableEnumNumber(formValue.applicantGoalFlags);
  const installableAreaSquareMeter =
    formValue.installableAreaSquareMeter !== "" &&
    formValue.installableAreaSquareMeter != null
      ? Number(formValue.installableAreaSquareMeter)
      : null;
  const plantCapacity = parsePlantCapacityForApi(formValue.plantCapacity);
  const loanGuaranteeChequeIntent =
    formValue.needsLoan && formValue.loanGuaranteeChequeIntent
      ? Number(formValue.loanGuaranteeChequeIntent)
      : null;

  return {
    powerPlantType,
    PowerPlantType: powerPlantType,
    locationUsageType,
    LocationUsageType: locationUsageType,
    ownershipStatus,
    OwnershipStatus: ownershipStatus,
    platformAcquaintanceSource,
    PlatformAcquaintanceSource: platformAcquaintanceSource,
    applicantGoalFlags,
    ApplicantGoalFlags: applicantGoalFlags,
    installableAreaSquareMeter,
    InstallableAreaSquareMeter: installableAreaSquareMeter,
    plantCapacity,
    PlantCapacity: plantCapacity,
    LoanGuaranteeChequeIntent: loanGuaranteeChequeIntent,
  };
};

/** Coerce FileUploader value to a stored URL string. */
export const toUploadedUrlString = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value.url || value.path || value.value || value.fullUrl || "";
  }
  return String(value);
};

/** Prefer a non-empty form upload; otherwise keep the value already stored on the request. */
export const resolveUploadedUrl = (formValue, existingValue) => {
  const fromForm = toUploadedUrlString(formValue);
  if (fromForm) return fromForm;
  if (typeof existingValue === "string") return existingValue;
  return toUploadedUrlString(existingValue);
};

/** Normalize uploaded file path fields on request (camelCase / PascalCase). */
export const normalizeRequestFileFields = (rawRequest, pageBaseUrl = "") => {
  if (!rawRequest) return rawRequest;
  const normalizePath = (value) => normalizeStoredFilePath(value, pageBaseUrl);
  const nationalCardBackUrl = normalizePath(
    pickString(rawRequest, "nationalCardBackUrl", "NationalCardBackUrl")
  );
  const birthFromApi = normalizePath(
    pickString(rawRequest, "birthCertificateUrl", "BirthCertificateUrl")
  );
  const birthFieldPresent =
    Object.prototype.hasOwnProperty.call(rawRequest, "birthCertificateUrl") ||
    Object.prototype.hasOwnProperty.call(rawRequest, "BirthCertificateUrl");
  // Legacy: before BirthCertificateUrl existed, شناسنامه lived in NationalCardBackUrl.
  const birthCertificateUrl = birthFieldPresent
    ? birthFromApi
    : birthFromApi || nationalCardBackUrl;
  return {
    ...rawRequest,
    electricBoillUrl: normalizePath(
      pickString(rawRequest, "electricBoillUrl", "ElectricBoillUrl")
    ),
    plantInstallImage1Url: normalizePath(
      pickString(rawRequest, "plantInstallImage1Url", "PlantInstallImage1Url")
    ),
    plantInstallImage2Url: normalizePath(
      pickString(rawRequest, "plantInstallImage2Url", "PlantInstallImage2Url")
    ),
    nationalCardUrl: normalizePath(
      pickString(rawRequest, "nationalCardUrl", "NationalCardUrl")
    ),
    nationalCardBackUrl: birthFieldPresent
      ? nationalCardBackUrl
      : birthCertificateUrl && nationalCardBackUrl === birthCertificateUrl
        ? ""
        : nationalCardBackUrl,
    birthCertificateUrl,
    homeOwnerShipDocUrl: normalizePath(
      pickString(rawRequest, "homeOwnerShipDocUrl", "HomeOwnerShipDocUrl")
    ),
    boardConsentDocumentUrl: normalizePath(
      pickString(rawRequest, "boardConsentDocumentUrl", "BoardConsentDocumentUrl")
    ),
    authorizedRepresentativeLetterUrl: normalizePath(
      pickString(
        rawRequest,
        "authorizedRepresentativeLetterUrl",
        "AuthorizedRepresentativeLetterUrl"
      )
    ),
    agreementDocumentUrl: normalizePath(
      pickString(rawRequest, "agreementDocumentUrl", "AgreementDocumentUrl")
    ),
  };
};

/** Normalize customer request payload for edit/create form dropdowns. */
export const normalizeCustomerRequestForForm = (rawRequest) => {
  if (!rawRequest) return rawRequest;
  return normalizeRequestEnumFields(
    normalizeRequestFileFields({
      ...rawRequest,
      id: rawRequest.id ?? rawRequest.Id,
      provinceId: rawRequest.provinceId ?? rawRequest.ProvinceId ?? "",
      cityId: rawRequest.cityId ?? rawRequest.CityId ?? "",
      electricBoillNumber: pickString(
        rawRequest,
        "electricBoillNumber",
        "ElectricBoillNumber"
      ),
    })
  );
};

/** فیلدهای هویتی متقاضی از پروفایل کاربر (camelCase / PascalCase). */
export const getApplicantIdentityFields = (source) => {
  if (!source) return {};
  const gender = source.gender ?? source.Gender;
  const birthDate = source.birthDate ?? source.BirthDate;
  return {
    nationalCode: source.nationalCode ?? source.NationalCode ?? "",
    fatherName: source.fatherName ?? source.FatherName ?? "",
    birthDateTitle: birthDate ? toJalaliDate(birthDate) : "",
    userName: source.userName ?? source.UserName ?? "",
    genderTitle:
      source.genderTitle ??
      source.GenderTitle ??
      getGenderDisplay(gender) ??
      "",
    email: source.email ?? source.Email ?? "",
    telephon: source.telephon ?? source.Telephon ?? "",
    agreementKindTitle:
      source.agreementKindTitle ?? source.AgreementKindTitle ?? "",
  };
};

export const getRequestRowId = (row) => {
  if (!row) return null;
  const id = row.id ?? row.Id;
  return id != null && id !== "" ? Number(id) || id : null;
};

export const buildCheckNewRequestEditPath = (rowOrId, listPath) => {
  const id =
    typeof rowOrId === "object" ? getRequestRowId(rowOrId) : rowOrId;
  if (id == null || id === "") return null;
  return `${listPath}/${id}`;
};

export const buildCheckNewRequestModifyPath = (
  rowOrId,
  configKey = "solarentOperator"
) => {
  const cfg = CHECK_NEW_REQUEST_CONFIG[configKey];
  if (!cfg?.listPath) return null;
  return buildCheckNewRequestEditPath(rowOrId, cfg.listPath);
};

/** Open standalone CheckNewRequest modify page in a new tab (same origin; shares localStorage JWT). */
export const openCheckNewRequestModifyPath = (path) => {
  if (!path) return false;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  window.open(normalized, "_blank", "noopener");
  return true;
};

/** Unwrap GetById ApiResult → page model with request + lookup lists. */
export const unwrapCheckNewRequestPayload = (response) => {
  if (!response || typeof response !== "object") return response;

  const candidates = [
    response,
    response.data,
    response.Data,
    response.data?.data,
    response.data?.Data,
    response.Data?.data,
  ].filter(Boolean);

  const hasPageModelShape = (candidate) =>
    Boolean(
      candidate?.request ||
        candidate?.Request ||
        candidate?.provinceList ||
        candidate?.ProvinceList ||
        candidate?.cityList ||
        candidate?.CityList ||
        candidate?.plantCapacity ||
        candidate?.PlantCapacity ||
        candidate?.plantKind ||
        candidate?.PlantKind ||
        candidate?.powerPlantType ||
        candidate?.PowerPlantType
    );

  for (const candidate of candidates) {
    if (hasPageModelShape(candidate)) {
      return candidate;
    }
  }

  return response.data ?? response.Data ?? response;
};

export const toDropdownArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    try {
      return Array.from(value);
    } catch {
      return Object.values(value);
    }
  }
  return [];
};

/** Backend enum lists use names (GridConnected); form values use numeric strings (1). */
export const normalizeEnumDropdownList = (raw, nameToNumberMap = null) =>
  toDropdownArray(raw).map((item) => {
    const rawValue = String(item?.value ?? item?.Value ?? "").trim();
    const mapped =
      nameToNumberMap && nameToNumberMap[rawValue] != null
        ? String(nameToNumberMap[rawValue])
        : rawValue;
    return {
      name: item?.name ?? item?.Name ?? "",
      value: mapped,
    };
  });

export const normalizeProvinceDropdownList = (list) =>
  (Array.isArray(list) ? list : []).map((item) => ({
    id: String(item?.id ?? item?.Id ?? ""),
    title:
      item?.title ??
      item?.Title ??
      item?.provinceName ??
      item?.ProvinceName ??
      "",
  }));

export const normalizeCityDropdownList = (list) =>
  (Array.isArray(list) ? list : []).map((item) => ({
    id: String(item?.id ?? item?.Id ?? ""),
    cityName: item?.cityName ?? item?.CityName ?? "",
    provinceId: item?.provinceId ?? item?.ProvinceId,
  }));

export const resolveGeoLookupData = (...sources) => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    const provinceRaw =
      source.provinceList ??
      source.ProvinceList ??
      source.data?.provinceList ??
      source.data?.ProvinceList;
    const cityRaw =
      source.cityList ??
      source.CityList ??
      source.data?.cityList ??
      source.data?.CityList;
    if (
      (Array.isArray(provinceRaw) && provinceRaw.length > 0) ||
      (Array.isArray(cityRaw) && cityRaw.length > 0)
    ) {
      return {
        provinceList: normalizeProvinceDropdownList(provinceRaw),
        cityList: normalizeCityDropdownList(cityRaw),
      };
    }
  }
  return { provinceList: [], cityList: [] };
};

/** Normalize GetById / loader payload (camelCase + PascalCase). */
export const normalizeCheckNewRequestPageData = (payload) => {
  const unwrapped = unwrapCheckNewRequestPayload(payload);
  if (!unwrapped || typeof unwrapped !== "object") return unwrapped;

  const rawRequest = unwrapped.request ?? unwrapped.Request;
  const request = rawRequest
    ? enrichApplicantIdentityFields(
        normalizeRequestEnumFields(
          normalizeRequestFileFields({
            ...normalizeAssignmentPermissions(rawRequest),
            id: rawRequest.id ?? rawRequest.Id,
            provinceId: rawRequest.provinceId ?? rawRequest.ProvinceId ?? "",
            cityId: rawRequest.cityId ?? rawRequest.CityId ?? "",
            fatherName:
              pickString(rawRequest, "fatherName", "FatherName") || "",
            birthDate: parseApplicantBirthDate(
              rawRequest.birthDate ?? rawRequest.BirthDate
            ),
            gender: normalizeGenderFormValue(
              rawRequest.gender ?? rawRequest.Gender
            ),
          })
        )
      )
    : null;

  const geo = resolveGeoLookupData(unwrapped);

  return {
    ...unwrapped,
    request,
    followUps: unwrapped.followUps ?? unwrapped.FollowUps ?? [],
    requestUpdateHistoryList:
      unwrapped.requestUpdateHistoryList ??
      unwrapped.RequestUpdateHistoryList ??
      [],
    provinceList: geo.provinceList,
    cityList: geo.cityList,
    supervisorList: unwrapped.supervisorList ?? unwrapped.SupervisorList ?? [],
    stepList: unwrapped.stepList ?? unwrapped.StepList ?? [],
    feederList: unwrapped.feederList ?? unwrapped.FeederList ?? [],
    substationList: unwrapped.substationList ?? unwrapped.SubstationList ?? [],
    powerPlantType: normalizeEnumDropdownList(
      unwrapped.powerPlantType ?? unwrapped.PowerPlantType,
      POWER_PLANT_TYPE_BY_NAME
    ),
    locationUsageType: normalizeEnumDropdownList(
      unwrapped.locationUsageType ?? unwrapped.LocationUsageType,
      LOCATION_USAGE_TYPE_BY_NAME
    ),
    ownershipStatus: normalizeEnumDropdownList(
      unwrapped.ownershipStatus ?? unwrapped.OwnershipStatus,
      OWNERSHIP_STATUS_BY_NAME
    ).filter(
      (item) =>
        item.value !== "2" &&
        item.value !== "Tenant" &&
        item.name !== "مستأجر هستم"
    ),
    plantCapacity: normalizeEnumDropdownList(
      unwrapped.plantCapacity ?? unwrapped.PlantCapacity
    ),
    plantKind: normalizeEnumDropdownList(
      unwrapped.plantKind ?? unwrapped.PlantKind
    ),
    requestConfirm: normalizeEnumDropdownList(
      unwrapped.requestConfirm ?? unwrapped.RequestConfirm
    ),
    agreementKind: normalizeEnumDropdownList(
      unwrapped.agreementKind ?? unwrapped.AgreementKind
    ),
    baseUrl: unwrapped.baseUrl ?? unwrapped.BaseUrl,
  };
};

export const getSavedRequestConfirm = (request) =>
  request?.requestConfirm ?? request?.RequestConfirm;

export const getSavedStepInfoId = (request) =>
  request?.stepInfoId ?? request?.StepInfoId;

/** True when request was rejected/canceled by operator or admin. */
export const isRequestCanceled = (request) => {
  if (!request) return false;

  const confirm = getSavedRequestConfirm(request);
  const confirmTitle = String(
    request?.requestConfirmTitle ?? request?.RequestConfirmTitle ?? ""
  ).trim();
  const normalized = String(confirm ?? "");

  return (
    normalized === "Canceled" ||
    normalized === "2" ||
    normalized === "Rejected" ||
    normalized === "3" ||
    confirmTitle.includes("لغو شده") ||
    confirmTitle.includes("رد شده") ||
    confirmTitle === "لغو" ||
    confirmTitle === "رد"
  );
};

/** Operator / management may edit request data in any workflow stage. */
export const canOperatorEditRequestFields = (request, assignmentSource) =>
  (assignmentSource === "SolarentOperator" || assignmentSource === "Management") &&
  !isRequestCanceled(request);

/** True while user may still edit/submit initial review. */
export const isInitialReviewEditable = (
  request,
  { forceEditable = false } = {}
) => {
  if (forceEditable) return true;

  const requestConfirm = getSavedRequestConfirm(request);

  if (requestConfirm == null || requestConfirm === "") {
    return true;
  }
  const normalized = String(requestConfirm);
  if (normalized === "NotChecked" || normalized === "0") {
    return true;
  }

  // اگر تایید اولیه ناقص مانده (جاری ولی هنوز روی مرحله ۱)، اجازه تکمیل دوباره بده
  const stepInfoId = Number(
    request?.stepInfoId ?? request?.StepInfoId ?? request?.stepId ?? 0
  );
  if (
    (normalized === "InProcess" || normalized === "1") &&
    stepInfoId === 1
  ) {
    return true;
  }

  if (normalized === "PendingFollowUp" || normalized === "6") {
    return true;
  }

  // سایر وضعیت‌ها = بررسی اولیه انجام شده
  return false;
};

export const isInitialReviewComplete = (request) =>
  Boolean(request) && !isInitialReviewEditable(request);
